import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from '../../toasts';
import DebtService from '../../services/debt.service';
import { getError, showCurrency } from '../../utils';
import { debtEstado, DEBT_ESTADO_TONE, DEBT_ESTADO_LABEL } from '../../utils/debtEstado';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import {
	Section,
	StatStrip,
	DataTable,
	IdChip,
	Pill,
	ProgressBar,
	RowActionsMenu,
	Button,
} from '../../components/EntityList';
import { useLabels } from '../../hooks/useLabels';

function formatDate(date) {
	return date ? moment.utc(date).format('DD/MM/YYYY') : '';
}

function formatMoney(currency, amount) {
	return `${showCurrency(currency) || ''} ${Number(amount || 0).toLocaleString('es-UY')}`.trim();
}

function AccountDebts({ accountId, rows, loading, error, onRefetch }) {
	const { t } = useLabels();
	const navigate = useNavigate();
	const location = useLocation();

	// Sends the editor back to this exact tab on save/cancel instead of letting
	// it land on the newly created debt's detail page.
	const returnTo = () => encodeURIComponent(location.pathname + location.search);
	const [deletingDebt, setDeletingDebt] = useState(null);

	const stats = useMemo(() => {
		const activas = rows.filter((debt) => debtEstado(debt) === 'activa').length;
		const vencidas = rows.filter((debt) => debtEstado(debt) === 'vencida').length;
		const saldoByCurrency = new Map();
		rows.forEach((debt) => {
			const key = debt.currency || '—';
			saldoByCurrency.set(key, (saldoByCurrency.get(key) || 0) + (debt.saldo || 0));
		});
		return [
			{ label: 'Activas', value: activas },
			{
				label: 'Saldo total',
				value: [...saldoByCurrency.entries()].map(([currency, saldo]) => formatMoney(currency, saldo)).join(' · ') || '—',
				mono: true,
			},
			{ label: 'Vencidas', value: vencidas },
		];
	}, [rows]);

	async function handleConfirmDelete() {
		try {
			await DebtService.delete(deletingDebt._id);
			toast.success('Deuda Eliminada');
			onRefetch();
		} catch (err) {
			toast.error(getError(err));
		} finally {
			setDeletingDebt(null);
		}
	}

	const columns = [
		{ key: 'id', header: 'ID', render: (debt) => <IdChip>{debt.customId}</IdChip> },
		{ key: 'concept', header: 'Concepto', render: (debt) => debt.concept },
		{ key: 'date', header: 'Inicio', render: (debt) => formatDate(debt.date) },
		{ key: 'endDate', header: 'Fin', render: (debt) => formatDate(debt.endDate) },
		{
			key: 'amount',
			header: 'Monto',
			className: 'tw:text-right',
			render: (debt) => <span className='tw:font-mono tw:tabular-nums'>{formatMoney(debt.currency, debt.amount)}</span>,
		},
		{
			key: 'cuotas',
			header: 'Cuotas',
			render: (debt) => (
				<div className='tw:min-w-[90px]'>
					<div className='tw:mb-1 tw:text-[12px] tw:tabular-nums'>
						{debt.cuotasPagas || 0}/{debt.cuotasTotal || 0}
					</div>
					<ProgressBar value={debt.cuotasPagas || 0} max={debt.cuotasTotal || 0} />
				</div>
			),
		},
		{
			key: 'saldo',
			header: 'Saldo',
			className: 'tw:text-right',
			render: (debt) => <span className='tw:font-mono tw:tabular-nums'>{formatMoney(debt.currency, debt.saldo)}</span>,
		},
		{
			key: 'estado',
			header: 'Estado',
			render: (debt) => {
				const estado = debtEstado(debt);
				return <Pill tone={DEBT_ESTADO_TONE[estado]}>{DEBT_ESTADO_LABEL[estado]}</Pill>;
			},
		},
	];

	const renderRowActions = (debt) => (
		<RowActionsMenu
			items={[
				{ label: 'Editar', onClick: () => navigate(`/AdminScreen/accounts/account/${accountId}/debts/${debt._id}/edit`) },
				{ label: 'Eliminar', onClick: () => setDeletingDebt(debt), danger: true },
			]}
		/>
	);

	return (
		<div className='tw:flex tw:flex-col tw:gap-4'>
			<StatStrip stats={stats} />
			<div className='tw:flex tw:justify-end'>
				<Button
					size='sm'
					variant='primary'
					onClick={() => navigate(`/AdminScreen/accounts/account/${accountId}/newDebt?returnTo=${returnTo()}`)}
				>
					Nueva deuda
				</Button>
			</div>
			<Section title='Deudas'>
				<DataTable
					columns={columns}
					rows={rows}
					loading={loading}
					error={error}
					onRowClick={(debt) => navigate(`/AdminScreen/debts/debt/${debt._id}`)}
					rowActions={renderRowActions}
					emptyMessage='No se encontraron deudas para esta cuenta.'
				/>
			</Section>

			<ConfirmationModal
				show={Boolean(deletingDebt)}
				handleClose={() => setDeletingDebt(null)}
				handleConfirm={handleConfirmDelete}
				title={t('title.deleteDebt')}
				description={t('msg.confirmDeleteDebtShort')}
				confirmCaption={t('action.delete')}
				cancelCaption={t('action.cancel')}
			/>
		</div>
	);
}

export default AccountDebts;
